import React, { useState, useEffect } from 'react';
import { useData } from '../DataContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useForm } from 'react-hook-form';
import useScrambleText from '../../hooks/useScrambleText';
import ImageUpload from '../ImageUpload';
import { Save, Loader2 } from 'lucide-react';

const AdminAbout = () => {
  const { aboutContent, updateAboutContent, loading } = useData();
  const { register, handleSubmit, setValue, reset, watch } = useForm({
    defaultValues: aboutContent
  });
  const h1Ref = useScrambleText("About Page Manager");
  const [imageUrl, setImageUrl] = useState(aboutContent.mainImage);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when aboutContent changes (e.g., after loading from API)
  useEffect(() => {
    reset(aboutContent);
    setImageUrl(aboutContent.mainImage);
  }, [aboutContent, reset]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await updateAboutContent({ ...data, mainImage: imageUrl });
    } catch (error) {
      console.error('Failed to update about content:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageChange = (newImageUrl) => {
    setImageUrl(newImageUrl);
    setValue('mainImage', newImageUrl);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 ref={h1Ref} className="text-3xl font-heading font-bold korov-gradient-text mb-2 cursor-pointer">About Page Manager</h1>
        <p className="text-muted-foreground">Update artist information and about page content</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Main Content Section */}
        <div className="bg-card border border-white/10 rounded-lg p-6 space-y-6">
          <h2 className="text-xl font-heading text-primary">Main Content</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Section Title</label>
              <Input 
                {...register('title', { required: true })} 
                placeholder="e.g., The Artist"
                className="bg-background/50"
                data-testid="about-title-input"
              />
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Main Heading</label>
              <Input 
                {...register('heading', { required: true })} 
                placeholder="e.g., Channeling the Old School Spirit"
                className="bg-background/50"
                data-testid="about-heading-input"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Main Image</label>
            <ImageUpload 
              value={imageUrl}
              onChange={handleImageChange}
              label="Upload About Image"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Or enter image URL:
            </p>
            <Input 
              value={imageUrl}
              onChange={(e) => handleImageChange(e.target.value)}
              placeholder="https://..."
              className="bg-background/50 mt-2"
              data-testid="about-image-input"
            />
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">First Paragraph</label>
            <Textarea 
              {...register('paragraph1', { required: true })} 
              placeholder="First paragraph about the artist..."
              className="bg-background/50 min-h-[120px]"
              data-testid="about-paragraph1-input"
            />
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Second Paragraph</label>
            <Textarea 
              {...register('paragraph2', { required: true })} 
              placeholder="Second paragraph about the artist..."
              className="bg-background/50 min-h-[120px]"
              data-testid="about-paragraph2-input"
            />
          </div>
        </div>

        {/* Statistics Section */}
        <div className="bg-card border border-white/10 rounded-lg p-6 space-y-6">
          <h2 className="text-xl font-heading text-primary">Statistics</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-sm font-heading text-gradient-platinum">Statistic 1</h3>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Value</label>
                <Input 
                  {...register('stat1Value', { required: true })} 
                  placeholder="e.g., 10+"
                  className="bg-background/50"
                  data-testid="about-stat1value-input"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Label</label>
                <Input 
                  {...register('stat1Label', { required: true })} 
                  placeholder="e.g., Years Active"
                  className="bg-background/50"
                  data-testid="about-stat1label-input"
                />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-heading text-gradient-platinum">Statistic 2</h3>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Value</label>
                <Input 
                  {...register('stat2Value', { required: true })} 
                  placeholder="e.g., Dharmar"
                  className="bg-background/50"
                  data-testid="about-stat2value-input"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Label</label>
                <Input 
                  {...register('stat2Label', { required: true })} 
                  placeholder="e.g., Label Home"
                  className="bg-background/50"
                  data-testid="about-stat2label-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            type="submit" 
            className="bg-primary text-black hover:bg-primary/80 hover:shadow-[0_0_20px_hsl(var(--primary)/0.4)] font-heading tracking-wider px-8"
            disabled={isSubmitting}
            data-testid="about-save-btn"
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </form>

      {/* Preview Section */}
      <div className="bg-card border border-white/10 rounded-lg p-6">
        <h2 className="text-xl font-heading text-primary mb-4">Current Content Preview</h2>
        <div className="space-y-4 text-sm">
          <div>
            <span className="text-muted-foreground">Title:</span>
            <span className="text-white ml-2">{aboutContent.title}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Heading:</span>
            <span className="text-white ml-2">{aboutContent.heading}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Stats:</span>
            <span className="text-white ml-2">{aboutContent.stat1Value} {aboutContent.stat1Label} | {aboutContent.stat2Value} {aboutContent.stat2Label}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAbout;
